import type {
    CategoryResult,
    CategoryVariables
} from '../../api/operations/get-category'
import type { SearchCriteria } from '@corratech/pylot-filters-and-sort'
import { SearchCriteriaFilterActions } from '@corratech/pylot-filters-and-sort'
import {
    SwrUsePaginatedQueryOutput,
    usePaginatedQuery
} from '../use-paginated-query'
import { SwrUseQueryOutput, useQuery } from '@pylot-data/hooks/use-query'
import type {
    CategoryTree,
    ProductInterface,
    Maybe
} from '@pylot-data/pylotschema'
import type { Products, QueryProductsArgs } from '@pylot-data/fwrdschema'
import { SWRConfiguration, SWRInfiniteConfiguration } from 'swr'
import { oldCategoryQuery, productsQuery } from './graphql/categoryQuery'
import type { RequestInit } from '@vercel/fetch'
import { GraphQLError } from '@pylot-data/graphqlFetch'
import { useEffect } from 'react'
import { useRouter } from 'next/router'

type UseCategoryResult = {
    category?: Maybe<CategoryTree>
    productsResult?: Maybe<Products>
    productItems: ProductInterface[]
    isLast: boolean
    isLoading: boolean
    error: GraphQLError | undefined
    categoriesSwrResult: SwrUseQueryOutput<CategoryResult>
    productsSwrResult: SwrUsePaginatedQueryOutput<{
        data: { products: Products }
    }>
}

export const useCategory = (
    urlPath: string,
    categoryUid: string,
    productsInput: QueryProductsArgs & { searchCriteria?: SearchCriteria[] },
    categorySwrOptions: SWRConfiguration,
    productSwrOptions: SWRInfiniteConfiguration = {},
    fetchOptions: RequestInit,
    disabled = false
): UseCategoryResult => {
    const { pageSize, searchCriteria = [], sort } = productsInput
    const router = useRouter()
    const categorySwrResult = useQuery<CategoryVariables, CategoryResult>(
        urlPath && !disabled ? oldCategoryQuery : null,
        {
            categoryFilter: {
                url_path: {
                    eq: urlPath
                }
            }
        },
        categorySwrOptions,
        fetchOptions
    )

    const processedSearchCriteria = [...searchCriteria]
    if (
        !searchCriteria.find((item: { attribute_code: string }) =>
            ['category_id', 'category_uid'].includes(item.attribute_code)
        )
    ) {
        processedSearchCriteria.push({
            attribute_code: 'category_uid',
            filter_action: SearchCriteriaFilterActions.EQ,
            filter_value: categoryUid
        })
    }

    const productsSwrResult = usePaginatedQuery<
        QueryProductsArgs & { searchCriteria?: SearchCriteria[] },
        { data: { products: Products } }
    >(
        !disabled && categoryUid ? productsQuery : null,
        {
            // `currentPage` will be automatically replaced with usePaginatedQuery's `size` prop
            currentPage: 1,
            pageSize,
            sort,
            searchCriteria: processedSearchCriteria
        },
        'currentPage',
        productSwrOptions,
        fetchOptions
    )

    useEffect(() => {
        const revalidateSWR = async () => {
            await categorySwrResult.mutate()
            await productsSwrResult.mutate()
        }
        revalidateSWR()
    }, [router.locale])

    const { size } = productsSwrResult
    const initialSizeOffset = (productSwrOptions.initialSize ?? 1) - 1

    // currentPageIndex is used to get data for the current page
    const currentPageIndex = size - 1 - initialSizeOffset

    const category = categorySwrResult.data?.data?.categories?.items?.[0]

    const mostRecentProductsQueryResult =
        productsSwrResult.data?.[currentPageIndex]?.data
    const productsResult = mostRecentProductsQueryResult?.products

    const isLast =
        mostRecentProductsQueryResult?.products?.page_info?.current_page ===
            mostRecentProductsQueryResult?.products?.page_info?.total_pages ??
        false

    const productItems =
        (Array.isArray(productsSwrResult?.data) &&
            productsSwrResult?.data
                ?.slice(0, size - initialSizeOffset)
                ?.reduce<ProductInterface[]>((acc, resultPage) => {
                    const productItemsLength =
                        resultPage.data?.products?.items?.length
                    // due to ?s used above, this condition will check that
                    // products.items exists AND has length > 0
                    if (productItemsLength) {
                        return [
                            ...acc,
                            // see comment above -> we know this is defined and contains Product Items
                            ...(resultPage.data?.products
                                ?.items! as ProductInterface[])
                        ]
                    }

                    return acc
                }, [])) ??
        []

    return {
        category,
        productsResult,
        productItems: productItems as ProductInterface[],
        isLast,
        isLoading: !categorySwrResult.data || !productsSwrResult.data,
        error:
            categorySwrResult.error ||
            productsSwrResult.error ||
            !categorySwrResult.data?.data?.categories,
        categoriesSwrResult: categorySwrResult,
        productsSwrResult
    }
}
